import React from "react";
import { Container } from "../../../../../components/Grid/Grid";
import { Svg, SVG_ICONS } from "../../../../../components/__common/Svg/Svg";
import { Typography } from "../../../../../components/__common/Typography/Typography";
import "./Summary.scss";
import { useWindowSize } from "../../../../../hooks/window-hooks";
import { LatestRequests } from "./LatestRequests";
import { SummaryEntity } from "../../../../../types/mydata";
import { t } from "i18next";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { SurveyModal } from "../../../../../components/SurveyModal/SurveyModal";
import { NoDataMessage } from "../../../../../components/NoDataMessage/NoDataMessage";
import FocusTrapping from "../../../../../components/__hoc/FocusTrapping";
import { useSurveyModal } from "../../../../../hooks/use-survey-modal";

interface Props {
  summary: SummaryEntity;
  isFromChina?: boolean;
  isRestricted?: boolean;
  saCountry?: string;
}

export const Summary: React.FC<Props> = (props) => {
  const { isMobile } = useWindowSize();
  const summaryIconSize = isMobile ? 32 : 40;
  const {
    summary,
    isFromChina = false,
    isRestricted = false,
    saCountry,
  } = props;
  const { surveyModalOpen, handleSurveyOnClose } = useSurveyModal();

  /**
   * Formats the count display for summary cards.
   * Returns "00" for China region, pads numbers < 100 with leading zero,
   * or returns the number as-is for values >= 100.
   */
  const formatCountDisplay = (count: number, isChina: boolean): string => {
    if (isChina) {
      return "00";
    }
    return count < 100 ? String(count).padStart(2, "0") : String(count);
  };

  const handleRestriction = () => {
    if (isFromChina)
      return (
        <>
          {t("PRIVACY_MYDATA_MSG_CHINA")} <br />
          <Trans
            i18nKey="PRIVACY_MYDATA_MSG_CHINA_CNPP_LINK"
            components={{
              a: (
                 
                <a target="_blank" href="https://privacy.samsung.com.cn/" />
              ),
            }}
          />
        </>
      );
    if (saCountry === "USA")
      return (
        <Trans
          i18nKey="PRIVACY_MYDATA_MSG_CHILDREN_USA"
          components={{
            a: (
               
              <a target="_blank" href="https://www.samsung.com/us/privacy" />
            ),
          }}
        />
      );
    return t("PRIVACY_MYDATA_MSG_NOT_SUPPORTED");
  };
  return (
    <Container>
      <FocusTrapping
        modalId="survey-modal-summary-page"
        isActive={surveyModalOpen}
        onHandleClose={handleSurveyOnClose}
      >
        <SurveyModal
          isOpen={surveyModalOpen}
          id="survey-modal-summary-page"
          onHide={handleSurveyOnClose}
        />
      </FocusTrapping>

      <div
        className={classNames("mydata-summary", {
          china: isFromChina || isRestricted,
        })}
      >
        <div className="mydata-summary__counts">
          <div className="mydata-summary__counts__item">
            <Svg
              icon={SVG_ICONS.MYDATA_INPROGRESS}
              width={summaryIconSize}
              height={summaryIconSize}
            />
            <Typography
              variant="body2"
              className="mydata-summary__counts__item__text subText"
            >
              {t("PRIVACY_MYDATA_SUMMARY_LABEL_PROCESSING")}
            </Typography>
            <Typography
              variant="h3"
              className="mydata-summary__counts__item__count"
            >
              {formatCountDisplay(
                summary.count.total_count_inprogress,
                isFromChina
              )}
            </Typography>
          </div>
          <div className="spacer"></div>
          <div className="mydata-summary__counts__item">
            <Svg
              icon={SVG_ICONS.MYDATA_COMPLETED}
              width={summaryIconSize}
              height={summaryIconSize}
            />
            <Typography
              variant="body2"
              className="mydata-summary__counts__item__text subText"
            >
              {t("PRIVACY_MYDATA_SUMMARY_LABEL_COMPLETED")}
            </Typography>
            <Typography
              variant="h3"
              className="mydata-summary__counts__item__count"
            >
              {formatCountDisplay(
                summary.count.total_count_completed,
                isFromChina
              )}
            </Typography>
          </div>
        </div>

        {isRestricted && (
          <div className="mydata-summary__services-container china">
            <Svg icon={SVG_ICONS.CHINA_NODATA} width={64} height={64} />
            <Typography variant="body1" weight="w500">
              {handleRestriction()}
            </Typography>
          </div>
        )}
        {!isRestricted && (
          <>
            {summary.count.total_count === 0 ? (
              <NoDataMessage className="mt-40" />
            ) : (
              <LatestRequests summary={summary} />
            )}
          </>
        )}
      </div>
    </Container>
  );
};
